// src/pages/Dashboard/Admin/PagesPersonnalisees/components/PageFormDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaFileAlt,
  FaLink,
  FaMagic,
  FaSave,
  FaTimes,
} from "react-icons/fa";

import {
  QUILL_FORMATS,
  QUILL_MODULES,
  getDefaultPageForm,
  mapPageToForm,
  slugify,
  validatePageForm,
} from "../utils/pagesDashboardUtils";

function DrawerField({ label, children, hint, error }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      {children}

      {error ? (
        <p className="mt-1 text-xs leading-5 text-rose-600 dark:text-rose-300">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default function PageFormDrawer({
  isOpen,
  mode,
  page,
  submitting,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(getDefaultPageForm());
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit") {
      setFormData(mapPageToForm(page));
      setSlugTouched(true);
    } else {
      setFormData(getDefaultPageForm());
      setSlugTouched(false);
    }
  }, [isOpen, mode, page]);

  useEffect(() => {
    if (!isOpen || slugTouched) return;

    setFormData((prev) => ({
      ...prev,
      slug: slugify(prev.titre),
    }));
  }, [isOpen, slugTouched, formData.titre]);

  const errors = useMemo(() => validatePageForm(formData), [formData]);
  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  const title =
    mode === "edit" ? "Modifier une page" : "Créer une page personnalisée";

  const submitLabel =
    mode === "edit" ? "Enregistrer les modifications" : "Créer la page";

  const generatedPath = `/page/${slugify(formData.slug) || "chemin-de-la-page"}`;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "slug") setSlugTouched(true);

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const regenerateSlug = () => {
    setSlugTouched(false);

    setFormData((prev) => ({
      ...prev,
      slug: slugify(prev.titre),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    onSubmit?.(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Page CMS
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaFileAlt className="text-indigo-600 dark:text-indigo-300" />
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Définissez le titre public, le chemin de la page, son
              introduction riche et sa visibilité côté site.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <DrawerField label="Titre de la page *" error={errors.titre}>
              <input
                type="text"
                name="titre"
                value={formData.titre}
                onChange={handleChange}
                placeholder="Ex : Historique du club"
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
            </DrawerField>

            <DrawerField
              label="Chemin public *"
              error={errors.slug}
              hint={
                <span>
                  Chemin généré :{" "}
                  <span className="font-mono font-bold">{generatedPath}</span>
                </span>
              }
            >
              <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                <div className="relative">
                  <FaLink className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder="historique-du-club"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </div>

                <button
                  type="button"
                  onClick={regenerateSlug}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaMagic />
                  Générer
                </button>
              </div>
            </DrawerField>

            <DrawerField label="Introduction">
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                <ReactQuill
                  value={formData.introduction}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      introduction: value,
                    }))
                  }
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  className="[&_.ql-container]:min-h-[220px] [&_.ql-container]:max-h-[420px] [&_.ql-container]:overflow-y-auto dark:[&_.ql-toolbar]:border-slate-700 dark:[&_.ql-container]:border-slate-700"
                />
              </div>
            </DrawerField>

            <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800">
              <input
                type="checkbox"
                name="actif"
                checked={!!formData.actif}
                onChange={handleChange}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
              />

              <span>
                <span className="block text-sm font-bold text-slate-900 dark:text-white">
                  Page active
                </span>

                <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Une page inactive reste disponible en administration, mais
                  peut être masquée côté site public selon votre route frontend.
                </span>
              </span>
            </label>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : submitLabel}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}